import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { TranslatableText } from '@/components/ui/TranslatableText'
import type { IngressActiveItem, IngressHistoryItem } from './api'

function formatSize(sizeMB: number | null): string {
    if (sizeMB == null) return '-'
    if (sizeMB >= 1024) return `${(sizeMB / 1024).toFixed(2)} GB`
    return `${sizeMB.toFixed(2)} MB`
}

function formatDuration(mins: number | null): string {
    if (mins == null) return '-'
    if (mins < 60) return `${mins}m`
    const hours = Math.floor(mins / 60)
    const remaining = mins % 60
    if (hours < 24) return `${hours}h ${remaining}m`
    const days = Math.floor(hours / 24)
    return `${days}d ${hours % 24}h`
}

function stateClassName(state: string | null): string {
    if (!state) return 'border-gray-300 bg-gray-100 text-gray-600'
    const s = state.toLowerCase()
    if (s === 'complete' || s === 'completed') return 'border-green-500 bg-green-500/20 text-green-700'
    if (s === 'error' || s === 'failed') return 'border-red-500 bg-red-500/20 text-red-700'
    if (s === 'in progress' || s === 'transferring' || s === 'processing') return 'border-blue-500 bg-blue-500/20 text-blue-700'
    if (s === 'pending' || s === 'queued') return 'border-amber-500 bg-amber-500/20 text-amber-700'
    return 'border-gray-300 bg-gray-100 text-gray-600'
}

export function createActiveColumns(
    t: (key: string) => string
): ColumnDef<IngressActiveItem>[] {
    return [
        {
            accessorKey: 'objectId',
            header: t('ingressMonitoring.objectId'),
            cell: ({ row }) => (
                <div>
                    <div className="font-medium">{row.original.objectId}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={row.original.objectName}>
                        <TranslatableText text={row.original.objectName} />
                    </div>
                </div>
            ),
        },
        {
            accessorKey: 'vaultId',
            header: t('ingressMonitoring.vault'),
        },
        {
            accessorKey: 'location',
            header: t('ingressMonitoring.location'),
            cell: ({ row }) => row.original.location ? <TranslatableText text={row.original.location} /> : '-',
        },
        {
            accessorKey: 'objectSecondary',
            header: t('ingressMonitoring.type'),
            cell: ({ row }) => {
                const type = row.original.objectSecondary
                return <Badge variant="outline">{type ? <TranslatableText text={type} /> : '-'}</Badge>
            },
        },
        {
            accessorKey: 'percentageComplete',
            header: t('ingressMonitoring.progress'),
            cell: ({ row }) => {
                const pct = row.original.percentageComplete ?? 0
                return (
                    <div className="flex items-center gap-2 min-w-[120px]">
                        <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                            <div
                                className="h-full rounded-full bg-primary transition-all"
                                style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                        </div>
                        <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'currentState',
            header: t('ingressMonitoring.state'),
            cell: ({ row }) => {
                const state = row.original.currentState
                return (
                    <Badge variant="outline" className={stateClassName(state)}>
                        {state || 'Pending'}
                    </Badge>
                )
            },
        },
        {
            accessorKey: 'sizeMB',
            header: t('ingressMonitoring.size'),
            cell: ({ row }) => formatSize(row.original.sizeMB),
        },
        {
            accessorKey: 'datetime',
            header: t('ingressMonitoring.started'),
            cell: ({ row }) => {
                const dt = row.original.startDatetime || row.original.datetime
                if (!dt) return '-'
                return new Date(dt).toLocaleString()
            },
        },
        {
            accessorKey: 'latestDetail',
            header: t('ingressMonitoring.detail'),
            cell: ({ row }) => (
                <div className="text-sm max-w-[200px] truncate text-muted-foreground" title={row.original.latestDetail || ''}>
                    {row.original.latestDetail ? <TranslatableText text={row.original.latestDetail} /> : '-'}
                </div>
            ),
        },
    ]
}

export function createHistoryColumns(
    t: (key: string) => string
): ColumnDef<IngressHistoryItem>[] {
    return [
        {
            accessorKey: 'objectId',
            header: t('ingressMonitoring.objectId'),
            cell: ({ row }) => (
                <div>
                    <div className="font-medium">{row.original.objectId}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={row.original.objectDescription}>
                        <TranslatableText text={row.original.objectDescription || row.original.objectName} />
                    </div>
                </div>
            ),
        },
        {
            accessorKey: 'vaultName',
            header: t('ingressMonitoring.vault'),
            cell: ({ row }) => (
                <div className="text-sm">{row.original.vaultName ? String(row.original.vaultName).trim() : row.original.vaultId}</div>
            ),
        },
        {
            accessorKey: 'location',
            header: t('ingressMonitoring.location'),
            cell: ({ row }) => row.original.location ? <TranslatableText text={row.original.location} /> : '-',
        },
        {
            accessorKey: 'datetime',
            header: t('ingressMonitoring.completed'),
            cell: ({ row }) => {
                if (!row.original.datetime) return '-'
                return new Date(row.original.datetime).toLocaleString()
            },
        },
        {
            accessorKey: 'percentageComplete',
            header: t('ingressMonitoring.status'),
            cell: () => (
                <Badge variant="outline" className="border-green-500 bg-green-500/20 text-green-700">{t('ingressMonitoring.complete', 'Complete')}</Badge>
            ),
        },
    ]
}
